import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import api from '../services/api';

interface PdfViewerProps {
  url: string;
  title?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ url, title = 'Document PDF' }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPdf();
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPdf = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch PDF with authentication
      const response = await api.get(url, {
        responseType: 'blob',
      });
      
      // Check if response is actually a blob
      if (response.data instanceof Blob) {
        // Create blob URL
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } else {
        // If not a blob, it might be an error response
        setError('Format de réponse invalide');
      }
    } catch (err: any) {
      console.error('Error loading PDF:', err);
      // Try to parse error from blob if response is JSON
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const errorData = JSON.parse(text);
          setError(errorData.error || 'Erreur lors du chargement du PDF');
        } catch {
          setError('Erreur lors du chargement du PDF');
        }
      } else {
        setError(err.response?.data?.error || err.message || 'Erreur lors du chargement du PDF');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${title}.pdf`;
      link.click();
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="600px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button size="small" onClick={loadPdf}>
          Réessayer
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box position="relative">
      <Box position="absolute" top={8} right={8} zIndex={1}>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          size="small"
          onClick={handleDownload}
        >
          Télécharger
        </Button>
      </Box>
      <iframe
        src={pdfUrl || ''}
        width="100%"
        height="600px"
        style={{ border: 'none' }}
        title={title}
      />
    </Box>
  );
};

export default PdfViewer;